import { RadarIcon } from "@signet/ui";
import { Navigate } from "@tanstack/react-router";
import { defineWebModule, lazyPage } from "@signet/web-kit";
import { str } from "./api.ts";
import "./research.css";

// Loaded when first opened (web-kit lazy.ts).
const OutliersPage = lazyPage(() => import("./outliers.tsx"), "OutliersPage");
const NicheCheckPage = lazyPage(() => import("./niche-check.tsx"), "NicheCheckPage");
const CreatorsPage = lazyPage(() => import("./creators.tsx"), "CreatorsPage");
const NichesPage = lazyPage(() => import("./niches.tsx"), "NichesPage");
const HikerUsageCard = lazyPage(() => import("./usage.tsx"), "HikerUsageCard");

// The API Usage tab's old address, kept so bookmarks and sent links still
// open it: it is HikerAPI's card on /usage since 2026-10-01.
function OldUsageTab() {
  return <Navigate to="/usage" replace />;
}

/* ──────────────────────────────────────────────────────────────────────────
   The web half of Research: the Instagram outlier finder. Its tabs, in this
   order: Viral Vault, Niche check, Creators, Niches. What HikerAPI costs is a card on the
   API/AI Usage page (/usage), contributed through usagePanels.
   ────────────────────────────────────────────────────────────────────────── */

export default defineWebModule({
  id: "research",
  nav: { label: "Research", icon: RadarIcon, to: "/research", needs: "research.outlier:view" },
  routes: [
    {
      path: "/research",
      component: OutliersPage,
      needs: "research.outlier:view",
      validateSearch: (raw) => ({ niches: str(raw.niches), min: str(raw.min), within: str(raw.within), sort: str(raw.sort) }),
    },
    // Viewing is Viral Vault's view, as on the server; the tab itself only shows to people who can remove.
    { path: "/research/check", component: NicheCheckPage, needs: "research.outlier:view" },
    {
      path: "/research/creators",
      component: CreatorsPage,
      needs: "research.account:view",
      validateSearch: (raw) => ({ niche: str(raw.niche), q: str(raw.q), add: str(raw.add), flagged: str(raw.flagged), handle: str(raw.handle), by: str(raw.by) }),
    },
    { path: "/research/niches", component: NichesPage, needs: "research.niche:view" },
    { path: "/research/usage", component: OldUsageTab, needs: "research.login:view" },
  ],
  commands: [
    { id: "research.outliers", label: "Viral Vault", to: "/research", needs: "research.outlier:view", hint: "Research", keywords: "reels instagram viral breakout trending" },
    { id: "research.check", label: "Niche check", to: "/research/check", needs: "research.outlier:edit", hint: "Research", keywords: "viral vault daily clean up remove wrong niche" },
    { id: "research.creators", label: "Research creators", to: "/research/creators", needs: "research.account:view", hint: "Research", keywords: "instagram handles watch" },
    { id: "research.niches", label: "Niches", to: "/research/niches", needs: "research.niche:view", hint: "Research", keywords: "topics groups" },
    // No API usage command: the page is a place under More for anyone who
    // can see this panel, with its own palette entry and these words on it.
  ],
  usagePanels: [
    {
      id: "research.hikerapi",
      label: "HikerAPI",
      needs: "research.login:view",
      order: 40,
      component: HikerUsageCard,
    },
  ],
});
